import React, { useState } from 'react';
import styled, { createGlobalStyle } from 'styled-components/macro';
import { FreeGallerySlider } from './FreeGallerySlider';
import { Intro } from './Intro';
import { ThumbnailsSection } from './ThumbnailsSection';
import { InfoPanel } from './InfoPanel';
import { VideoIntro } from './VideoIntro';
import { ApiClient } from 'src/client/helpers';
import { constructUrl } from 'src/shared/urls';
import { useQuery } from '@tanstack/react-query';
import { DescriptionText } from './styles';
import { ClassicSection } from './ClassicSection';
import { StaticSplashResponse } from 'src/@types/StaticSplashResponse';
import { useShowSfwSite } from 'src/client/helpers/useFeatureFlags';
import { Outro } from './Outro';
import { useSelector } from 'src/client/redux';
import { FooterBar } from 'src/client/containers/FooterBar/FooterBar';
import { Awards } from './Awards';
import { VideoBanner } from './VideoBanner';
import Helmet from 'src/client/containers/Helmet/Helmet';
import { usePromotions } from 'src/client/helpers/usePromotions';
import { PromoFooterBanner } from 'src/client/containers/Promo/PromoFooterBanner';
import { useStaticSplashContent } from './content/useStaticSplashContent';

const splashFetcher = (nonNude: boolean) => {
  const params = new URLSearchParams();

  if (nonNude) {
    params.append('nonNude', 'true');
  }

  return new ApiClient()
    .get(constructUrl(`/static-splash?${params.toString()}`))
    .then((res) => res.body as StaticSplashResponse);
};

export const StaticSplash = () => {
  const showSfwSite = useShowSfwSite();
  const response = useQuery(['static-splash', showSfwSite], () => splashFetcher(showSfwSite));
  const { domain } = useSelector((state) => state.site);
  const { seoText, brandColor, brandHoverColor } = useStaticSplashContent();
  const [isSplashAdVisible, setSplashAdVisible] = useState<boolean>(false);
  const { isPromoSplashFooterActive } = usePromotions();

  const { models = [], movies = [], galleries = [], classics = [], freeGalleries } = response?.data || {};

  return (
    <Wrapper brandColor={brandColor}>
      <Helmet id="homepage" variables={{ siteDomain: domain }} showSchema>
        <meta property="prerender:ttl" content="86400" />
      </Helmet>
      <GlobalBrandColor brandColor={brandColor} brandHoverColor={brandHoverColor} />
      <VideoIntro setSplashAdVisible={setSplashAdVisible} />
      <Intro />
      <ThumbnailsSection thumbnails={models} type="models" />

      <Wrapper>
        <FreeGallerySlider freeGalleries={freeGalleries} />
        <InfoPanel panelNumber="one" />
      </Wrapper>

      <ThumbnailsSection thumbnails={movies} type="movies" />
      <InfoPanel panelNumber="two" />
      <VideoBanner />
      <ThumbnailsSection thumbnails={galleries} type="galleries" />

      <SEODescriptionWrapper>
        <DescriptionText>{seoText}</DescriptionText>
      </SEODescriptionWrapper>

      <ClassicSection thumbnails={classics} />
      <Outro />
      <Awards />

      <FooterContainer>
        <FooterBar />
      </FooterContainer>

      {!isSplashAdVisible && isPromoSplashFooterActive && <PromoFooterBanner />}
    </Wrapper>
  );
};

// This is some new color thats not the normal brand color, and not part of the
// theme, unique to the splash page. This is shared with the header bar
// so we need to set this new css var at the root so everyone can use it.
const GlobalBrandColor = createGlobalStyle<{ brandColor: string; brandHoverColor: string }>`
  :root {
    --brand-color: ${(props) => props.brandColor};
    --brand-hover-color: ${(props) => props.brandHoverColor};
  }
`;

const Wrapper = styled.div<{ brandColor?: string }>`
  background-color: #000000;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  width: 100%;
  gap: 5px;
`;

const SEODescriptionWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 20px 0;
  margin-bottom: 10px;
`;

const FooterContainer = styled.div`
  & .seo-footer {
    background: none;
  }

  & .read-more {
    background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(0, 0, 0, 1) 70%);
  }
`;
